import { useState, useEffect } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { bonusAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Gift, FileText, Lock, BookOpen, Target, Scale, BarChart3, Briefcase, Shield, Calculator } from 'lucide-react';
import { Link } from 'react-router-dom';

const BonusPage = () => {
  const [bonusData, setBonusData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user } = useAuth();

  useEffect(() => {
    loadBonus();
  }, []);

  const loadBonus = async () => {
    try {
      const response = await bonusAPI.getAll();
      setBonusData(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        setError('access_denied');
      } else {
        setError('error');
      }
    } finally {
      setLoading(false);
    }
  };

  const getIcon = (id) => {
    const icons = {
      '1': BookOpen,      // Guide Statut
      '2': Shield,        // Guide Retraite
      '3': FileText,      // Pack Courriers
      '4': Scale,         // Guide BIC/BNC
      '5': BarChart3,     // Atelier Dashboard
      '6': Target,        // Atelier 1K
      '7': Briefcase,     // Kit juridique
      '8': Calculator,    // Étude PER
      '9': FileText       // Facture électronique
    };
    return icons[id] || Gift;
  };

  const getIconColors = (id) => {
    const colors = {
      '1': { bg: 'bg-emerald-100', text: 'text-emerald-600' },
      '2': { bg: 'bg-blue-100', text: 'text-blue-600' },
      '3': { bg: 'bg-purple-100', text: 'text-purple-600' },
      '4': { bg: 'bg-amber-100', text: 'text-amber-600' },
      '5': { bg: 'bg-cyan-100', text: 'text-cyan-600' },
      '6': { bg: 'bg-rose-100', text: 'text-rose-600' },
      '7': { bg: 'bg-indigo-100', text: 'text-indigo-600' },
      '8': { bg: 'bg-teal-100', text: 'text-teal-600' },
      '9': { bg: 'bg-orange-100', text: 'text-orange-600' }
    };
    return colors[id] || { bg: 'bg-gray-100', text: 'text-gray-600' };
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-gray-200 rounded w-48"></div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="h-40 bg-gray-200 rounded-xl"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error === 'access_denied' || !user?.has_paid) {
    return (
      <div className="space-y-6" data-testid="bonus-page-locked">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Votre Patrithèque</h1>
          <p className="text-gray-500">La Patrithèque MON AE — Vos ressources pour mieux comprendre et préparer votre avenir professionnel</p>
        </div>

        <Card className="max-w-2xl mx-auto border-0 shadow-lg">
          <CardContent className="p-12 text-center">
            <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <Lock className="h-10 w-10 text-gray-400" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-4">Accès réservé aux membres</h2>
            <p className="text-gray-500 mb-8 max-w-md mx-auto">
              La Patrithèque est accessible uniquement aux membres du Pack Auto-Entrepreneur.
              Débloquez les ressources de la Patrithèque MON AE.
            </p>
            <Link to="/demo?step=4">
              <Button 
                size="lg" 
                className="bg-[#10B981] hover:bg-[#059669] text-white"
                data-testid="unlock-bonus-btn"
              >
                S'abonner — 299€/an
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="bonus-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">Votre Patrithèque</h1>
          <p className="text-gray-500" data-testid="bonus-subtitle">La Patrithèque MON AE — Vos ressources pour mieux comprendre et préparer votre avenir professionnel</p>
        </div>
      </div>

      {/* Bonus Grid - 2 colonnes */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        {bonusData?.bonus?.map((bonus) => {
          const Icon = getIcon(bonus.id);
          const colors = getIconColors(bonus.id);
          return (
            <Card 
              key={bonus.id} 
              className="border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
              data-testid={`bonus-${bonus.id}`}
            >
              <CardContent className="p-5">
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 ${colors.bg} rounded-xl flex items-center justify-center flex-shrink-0`}>
                    <Icon className={`h-6 w-6 ${colors.text}`} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-xs font-semibold text-[#10B981]">Ressource #{bonus.id}</span>
                    </div>
                    <h3 className="font-semibold text-gray-800 mb-1 leading-tight">{bonus.title}</h3>
                    <p className="text-sm text-gray-500 mb-3 line-clamp-2">{bonus.description}</p>
                    
                    {bonus.type === 'html' && bonus.url && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="border-gray-200 hover:bg-gray-50"
                        asChild
                      >
                        <a href={bonus.url} target="_blank" rel="noopener noreferrer">
                          <FileText className="h-4 w-4 mr-2" />
                          Consulter le guide
                        </a>
                      </Button>
                    )}
                    
                    {bonus.type === 'access' && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        className="border-gray-200 hover:bg-gray-50"
                        asChild
                      >
                        <a href="mailto:contact@capsulepatrimoine.com?subject=Accès%20canal%20privé%20de%20support">
                          <Gift className="h-4 w-4 mr-2" />
                          Nous contacter
                        </a>
                      </Button>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Message de fin - fond vert clair */}
      <Card className="bg-[#ecfdf5] border-[#10B981]/20">
        <CardContent className="p-5">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-[#10B981]/10 rounded-xl flex items-center justify-center flex-shrink-0">
              <Gift className="h-6 w-6 text-[#10B981]" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-800 mb-1">Merci pour votre confiance !</h3>
              <p className="text-sm text-gray-600">
                Vous avez accès à toutes les ressources de la Patrithèque MON AE. 
                Ces ressources sont régulièrement mises à jour pour refléter les dernières évolutions réglementaires.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BonusPage;
